import { projectId, publicAnonKey } from '/utils/supabase/info';

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-586f2897`;

export interface Asset {
  id: string;
  marker: string;
  url: string;
  urlEn?: string; // Optional English version
  alt?: string;
  altEn?: string; // Optional English alt text
  caption?: string; // Optional Hungarian caption
  captionEn?: string; // Optional English caption
  description?: string;
  formatting: {
    width?: string;
    height?: string;
    className?: string;
    style?: Record<string, string>;
    captionClassName?: string; // Optional Tailwind classes for caption
  };
  createdAt: string;
  updatedAt: string;
}

export const assetApi = {
  // Get all assets
  async getAssets(): Promise<Asset[]> {
    const response = await fetch(`${API_BASE}/assets`, {
      headers: {
        'Authorization': `Bearer ${publicAnonKey}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch assets');
    }
    
    const data = await response.json();
    return data.assets || [];
  },

  // Create asset
  async createAsset(asset: Omit<Asset, 'id' | 'createdAt' | 'updatedAt'>): Promise<Asset> {
    const response = await fetch(`${API_BASE}/assets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify(asset),
    });
    
    if (!response.ok) {
      throw new Error('Failed to create asset');
    }
    
    const data = await response.json();
    return data.asset;
  },

  // Update asset
  async updateAsset(id: string, updates: Partial<Asset>): Promise<Asset> {
    const response = await fetch(`${API_BASE}/assets/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update asset');
    }
    
    const data = await response.json();
    return data.asset;
  },

  // Delete asset
  async deleteAsset(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/assets/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${publicAnonKey}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete asset');
    }
  },

  // Upload asset image
  async uploadAssetImage(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/upload/asset`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${publicAnonKey}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload asset image');
    }

    const data = await response.json();
    return data.url;
  },
};

// Helper function to process asset markers in HTML content
export function processAssetMarkers(content: string, assets: Asset[], language: 'hu' | 'en' = 'hu'): string {
  let processedContent = content;
  
  assets.forEach(asset => {
    const marker = `[${asset.marker}]`;
    
    if (processedContent.includes(marker)) {
      // Select URL based on language (fallback to Hungarian if English not available)
      const imageUrl = (language === 'en' && asset.urlEn) ? asset.urlEn : asset.url;
      
      // Select alt text based on language (fallback to Hungarian if English not available)
      const altText = (language === 'en' && asset.altEn) ? asset.altEn : (asset.alt || asset.marker);
      
      // Select caption based on language (fallback to Hungarian if English not available)
      const caption = (language === 'en' && asset.captionEn) ? asset.captionEn : asset.caption;
      
      // Build style string
      const styleStr = asset.formatting.style 
        ? Object.entries(asset.formatting.style)
            .map(([key, value]) => `${key}: ${value}`)
            .join('; ')
        : '';
      
      // Build className
      const className = asset.formatting.className || '';
      
      // If there's a caption, wrapper (figure) gets the formatting classes
      // and caption inherits alignment from the wrapper
      if (caption) {
        const imgTag = `<img 
          src="${imageUrl}" 
          alt="${altText}"
          ${asset.formatting.width ? `width="${asset.formatting.width}"` : ''}
          ${asset.formatting.height ? `height="${asset.formatting.height}"` : ''}
        />`;
        
        // Build caption className - combine default with custom classes
        const captionClassName = asset.formatting.captionClassName 
          ? `asset-caption ${asset.formatting.captionClassName}`
          : 'asset-caption';
        
        const finalHtml = `<figure class="asset-figure ${className}" style="${styleStr}">
          ${imgTag}
          <figcaption class="${captionClassName}">${caption}</figcaption>
        </figure>`;
        
        processedContent = processedContent.replaceAll(marker, finalHtml);
      } else {
        // No caption: img gets all the formatting
        const imgTag = `<img 
          src="${imageUrl}" 
          alt="${altText}"
          class="${className}"
          style="${styleStr}"
          ${asset.formatting.width ? `width="${asset.formatting.width}"` : ''}
          ${asset.formatting.height ? `height="${asset.formatting.height}"` : ''}
        />`;
        
        processedContent = processedContent.replaceAll(marker, imgTag);
      }
    }
  });
  
  return processedContent;
}